import { Suspense, lazy, useEffect } from 'react';
import { HashRouter, Navigate, Route, Routes, BrowserRouter } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { resumeEnrichmentJobsFromStorage } from '@/modules/outreach-v2/services/enrichmentJobTracker';
import { AuthProvider } from '@/auth/AuthProvider';
import { LoginPage } from '@/auth/LoginPage';
import { RequireAuth } from '@/auth/RequireAuth';
import { SsoLandingPage } from '@/auth/SsoLandingPage';
import { AppShell } from '@/shell/AppShell';
import { getRouterMode } from '@/lib/route-mode';
import { appRoutes } from '@/pages/page-registry';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ReviewLandingPage } from '@/pages/review/ReviewLandingPage';
import { ReviewerInboxPage } from '@/pages/review/ReviewerInboxPage';
import AcceptInvitePage from '@/modules/iam/components/AcceptInvitePage';

// Standalone public demo of the agentic setup flow — no auth, no AppShell.
// Same component as /v2/jobs/new-role/agentic, just mounted on its own route
// for share-with-anyone YC demo purposes.
const AgenticDemoPage = lazy(() => import('@/app/v2/jobs/new-role/agentic/page'));

// Gradient UX-study — bare /login clone showing only the gradient rectangle.
const GradientStudy = lazy(() => import('@/pages/GradientStudy'));

// Full-page job launch + edit (replaces the /plan and /edit modal flows).
// Authed but mounted OUTSIDE AppShell — no sidebar/tab chrome, the page owns
// the whole viewport.
const JobEditFinalPage = lazy(() => import('@/app/v2/jobs/[id]/edit-final/page'));

// Agentic job workspace — chat pane + module tabs (sourcing/outreach/stages).
// Authed, OUTSIDE AppShell like edit-final; the page owns the whole viewport.
const JobManagePage = lazy(() => import('@/app/v2/jobs/[id]/manage/page'));
const JobManageV2Page = lazy(() => import('@/app/v2/jobs/[id]/manage-v2/page'));
const NewJobManageV2Page = lazy(() => import('@/app/v2/jobs/new/manage-v2/page'));
const SessionRoute = lazy(() => import('@/pages/SessionRoute'));
// Cross-organization pilot command center. Authenticated, approved-email only,
// and intentionally outside AppShell because it owns its founder dashboard UI.
const AdminDashboardPage = lazy(() => import('@/pages/admin/AdminDashboardPage'));
const CustomAvatarPage = lazy(() => import('@/pages/custom-avatar/CustomAvatarPage'));

function AdminDashboardRoute() {
    return (
        <Suspense fallback={null}>
            <AdminDashboardPage />
        </Suspense>
    );
}

// Job Studio — two job-creation spines (canvas + chat) on the gradient.
const StudioIndex = lazy(() => import('@/pages/studio/StudioIndex'));
const CanvasStudio = lazy(() => import('@/pages/studio/CanvasStudio'));
const CanvasStudioV2 = lazy(() => import('@/pages/studio/CanvasStudioV2'));
const IcpStudio = lazy(() => import('@/pages/studio/IcpStudio'));
const SummaryStudio = lazy(() => import('@/pages/studio/SummaryStudio'));
const ChatStudio = lazy(() => import('@/pages/studio/ChatStudio'));
const StudioMockups = lazy(() => import('@/pages/studio/StudioMockups'));
const PlanStudio = lazy(() => import('@/pages/studio/PlanStudio'));

// ICP UX-study mockups — five standalone, no-chrome pages for design review.
const IcpMockupIndex = lazy(() => import('@/pages/mockups/IcpMockupIndex'));
const IcpMockupSentence = lazy(() => import('@/pages/mockups/IcpMockupSentence'));
const IcpMockupFunnel = lazy(() => import('@/pages/mockups/IcpMockupFunnel'));
const IcpMockupSwipe = lazy(() => import('@/pages/mockups/IcpMockupSwipe'));
const IcpMockupDial = lazy(() => import('@/pages/mockups/IcpMockupDial'));
const IcpMockupAnnotated = lazy(() => import('@/pages/mockups/IcpMockupAnnotated'));
const IcpMockupConsole = lazy(() => import('@/pages/mockups/IcpMockupConsole'));
const IcpMockupPipeline = lazy(() => import('@/pages/mockups/IcpMockupPipeline'));
const IcpMockupSpec = lazy(() => import('@/pages/mockups/IcpMockupSpec'));
const IcpMockupPipelineA = lazy(() => import('@/pages/mockups/IcpMockupPipelineA'));
const IcpMockupPipelineA1 = lazy(() => import('@/pages/mockups/IcpMockupPipelineA1'));
const IcpMockupPipelineB = lazy(() => import('@/pages/mockups/IcpMockupPipelineB'));
const IcpMockupPipelineC = lazy(() => import('@/pages/mockups/IcpMockupPipelineC'));
const ChatBubbleStyles = lazy(() => import('@/pages/mockups/ChatBubbleStyles'));

// Key Actions Summary row treatments — UX-study for the edit-final page.
const KeyActionsMockup = lazy(() => import('@/pages/mockups/KeyActionsMockup'));

// Edit-final presentation studies — premium restyle (A) vs section cards (B),
// plus v2–v5 variations (editorial / inset wells / stage tinted / compact pro).
const EditFinalPremiumMockup = lazy(() => import('@/pages/mockups/EditFinalPremiumMockup'));
const EditFinalCardsMockup = lazy(() => import('@/pages/mockups/EditFinalCardsMockup'));
const EditFinalCardsV2 = lazy(() => import('@/pages/mockups/EditFinalCardsV2'));
const EditFinalCardsV3 = lazy(() => import('@/pages/mockups/EditFinalCardsV3'));
const EditFinalCardsV4 = lazy(() => import('@/pages/mockups/EditFinalCardsV4'));
const EditFinalCardsV5 = lazy(() => import('@/pages/mockups/EditFinalCardsV5'));
const PipelineChipsMockup = lazy(() => import('@/pages/mockups/PipelineChipsMockup'));
const EditFinalCardsV6 = lazy(() => import('@/pages/mockups/EditFinalCardsV6'));

// Hero-greeting animation UX-study — five entrance-motion ideas for the chat hero.
const HeroAnimations = lazy(() => import('@/pages/mockups/HeroAnimations'));

// Question-prompt UX-study mockups — three variants for the agent's
// "ask the user a clarification" UI.
const QuestionPromptIndex = lazy(() => import('@/pages/mockups/question-prompt/QuestionPromptIndex'));
const QuestionPromptCompactCard = lazy(() => import('@/pages/mockups/question-prompt/QuestionPromptCompactCard'));
const QuestionPromptChipRow = lazy(() => import('@/pages/mockups/question-prompt/QuestionPromptChipRow'));
const QuestionPromptBubble = lazy(() => import('@/pages/mockups/question-prompt/QuestionPromptBubble'));

// GLM-5.2 — search reimagined (juicebox-style: hard filters + LLM rank + branching)
const Glm52Index = lazy(() => import('@/pages/mockups/glm5-2/Glm52Index'));
const Glm52SourcingStudio = lazy(() => import('@/pages/mockups/glm5-2/SourcingStudio'));
const Glm52SearchFlow = lazy(() => import('@/pages/mockups/glm5-2/SearchFlow'));

// Search · reimagined — UX-study for the new filter+rank+branch search experience.
const SearchReimaginedIndex = lazy(() => import('@/pages/mockups/search-reimagined/SearchReimaginedIndex'));
const SearchV2 = lazy(() => import('@/pages/mockups/search-reimagined/SearchV2'));
const SearchCanvas = lazy(() => import('@/pages/mockups/search-reimagined/SearchCanvas'));
const SearchConsole = lazy(() => import('@/pages/mockups/search-reimagined/SearchConsole'));
const SearchFunnel = lazy(() => import('@/pages/mockups/search-reimagined/SearchFunnel'));
const TalentPoolFiltersMockups = lazy(() => import('@/pages/mockups/talent-pool-filters/TalentPoolFiltersMockups'));

// Search artifact · redesign — sleek monochrome (Attio/Stripe) restyle of the
// sourcing artifact: 2 tabs, Atlas verdicts, agree/disagree, end-of-pool CTA.
const SearchArtifactMockup = lazy(() => import('@/pages/mockups/search-artifact/SearchArtifactMockup'));

// Search strategy — transparent hard-filter pool funnel (new direction).
const SearchStrategyMockup = lazy(() => import('@/pages/mockups/search-strategy/SearchStrategyMockup'));
const SearchV3FeedbackMockup = lazy(() => import('@/pages/mockups/search-v3-feedback/SearchV3FeedbackMockup'));
const CodexSearchListMockup = lazy(() => import('@/pages/mockups/codex-search-list/CodexSearchListMockup'));
// Funnel format study — five ways to visualize the pool narrowing.
const FunnelFormatsPage = lazy(() => import('@/pages/mockups/funnel-formats/FunnelFormatsPage'));
// GLM · 10 modern horizontal funnel formats.
const GlmFunnelFormats = lazy(() => import('@/pages/mockups/glm/GlmFunnelFormats'));
// GLM · search list study (chat list → search list).
const GlmSearchListIndex = lazy(() => import('@/pages/mockups/glm-search-list/GlmSearchListIndex'));
const GlmSearchListRich = lazy(() => import('@/pages/mockups/glm-search-list/RichRows'));
const GlmSearchListCompact = lazy(() => import('@/pages/mockups/glm-search-list/CompactList'));
const GlmSearchListBoard = lazy(() => import('@/pages/mockups/glm-search-list/TriageBoard'));
const GlmSearchListSplit = lazy(() => import('@/pages/mockups/glm-search-list/SplitDetail'));

// Outreach tab · UX-revamp mockup — the state-aware "campaign cockpit" study.
const OutreachTabMockup = lazy(() => import('@/pages/mockups/outreach-tab/OutreachTabMockup'));
const UltimateSourcingMockup = lazy(
    () => import('@/pages/mockups/ultimate-sourcing/UltimateSourcingMockup'),
);
const UltimateSourcingCompetitorSnapshot = lazy(
    () => import('@/pages/mockups/ultimate-sourcing/UltimateSourcingCompetitorSnapshot'),
);

const Router = getRouterMode() === 'hash' ? HashRouter : BrowserRouter;

export function App() {
    // Re-attach the toast + poll for any bulk enrichment job still in flight from
    // a prior page load (the run lives in the async worker, not the browser).
    useEffect(() => {
        resumeEnrichmentJobsFromStorage();
    }, []);

    return (
        <AuthProvider>
            <Router>
                <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/login/:ssoToken" element={<SsoLandingPage />} />
                    {/* Public invite-acceptance landing. Token comes in the URL path.
              Page handles its own "log in first" bounce when the user isn't
              authenticated — kept outside RequireAuth so the validate-invite
              preview renders before sign-in. */}
                    <Route path="/accept-v2-invite/:token" element={<AcceptInvitePage />} />
                    {/* External reviewer flow — public OTP entry, then the reviewer-only inbox.
              These routes are OUTSIDE RequireAuth (which checks for org JWT) and
              outside AppShell (no sidebar). The axios interceptor uses the
              task-invite JWT for /v2/tasks/* and /v2/reports/* requests. */}
                    <Route path="/review/:orgKey" element={<ReviewLandingPage />} />
                    <Route path="/review/:orgKey/tasks" element={<ReviewerInboxPage />} />
                    {/* Public agentic-setup demo (no login). Mirrors the authenticated
              route at /v2/jobs/new-role/agentic but renders standalone. */}
                    <Route
                        path="/demo/agentic"
                        element={
                            <Suspense fallback={null}>
                                <AgenticDemoPage />
                            </Suspense>
                        }
                    />
                    {/* Gradient UX-study — public, no auth, no chrome. */}
                    <Route
                        path="/gradient-study"
                        element={
                            <Suspense fallback={null}>
                                <GradientStudy />
                            </Suspense>
                        }
                    />
                    {/* Job Studio UX-study — public, no auth, no chrome. */}
                    <Route
                        path="/studio"
                        element={
                            <Suspense fallback={null}>
                                <StudioIndex />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/studio/canvas"
                        element={
                            <Suspense fallback={null}>
                                <CanvasStudio />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/studio/canvas-v2"
                        element={
                            <Suspense fallback={null}>
                                <CanvasStudioV2 />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/studio/icp"
                        element={
                            <Suspense fallback={null}>
                                <IcpStudio />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/studio/summary"
                        element={
                            <Suspense fallback={null}>
                                <SummaryStudio />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/studio/chat"
                        element={
                            <Suspense fallback={null}>
                                <ChatStudio />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/studio/mockups"
                        element={
                            <Suspense fallback={null}>
                                <StudioMockups />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/studio/plan"
                        element={
                            <Suspense fallback={null}>
                                <PlanStudio />
                            </Suspense>
                        }
                    />
                    {/* ICP UX-study mockups — public, full-bleed, no auth. */}
                    <Route
                        path="/mockups"
                        element={
                            <Suspense fallback={null}>
                                <IcpMockupIndex />
                            </Suspense>
                        }
                    />
                    {/* Outreach tab · UX-revamp — the "campaign cockpit" study. */}
                    <Route
                        path="/mockup/outreach-tab"
                        element={
                            <Suspense fallback={null}>
                                <OutreachTabMockup />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockup/ultimate-sourcing"
                        element={
                            <Suspense fallback={null}>
                                <UltimateSourcingMockup />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockup/ultimate-sourcing/snapshot-competitor"
                        element={
                            <Suspense fallback={null}>
                                <UltimateSourcingCompetitorSnapshot />
                            </Suspense>
                        }
                    />
                    {/* Search · reimagined — filter + rank + branch UX study. */}
                    <Route
                        path="/mockups/search-reimagined"
                        element={
                            <Suspense fallback={null}>
                                <SearchReimaginedIndex />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/search-reimagined/v2"
                        element={
                            <Suspense fallback={null}>
                                <SearchV2 />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/search-reimagined/canvas"
                        element={
                            <Suspense fallback={null}>
                                <SearchCanvas />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/search-reimagined/console"
                        element={
                            <Suspense fallback={null}>
                                <SearchConsole />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/search-reimagined/funnel"
                        element={
                            <Suspense fallback={null}>
                                <SearchFunnel />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/talent-pool-filters"
                        element={
                            <Suspense fallback={null}>
                                <TalentPoolFiltersMockups />
                            </Suspense>
                        }
                    />
                    {/* Search artifact · redesign — sleek monochrome sourcing artifact. */}
                    <Route
                        path="/mockups/search-artifact"
                        element={
                            <Suspense fallback={null}>
                                <SearchArtifactMockup />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/search-strategy"
                        element={
                            <Suspense fallback={null}>
                                <SearchStrategyMockup />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/search-v3-feedback"
                        element={
                            <Suspense fallback={null}>
                                <SearchV3FeedbackMockup />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/codex-search-list"
                        element={
                            <Suspense fallback={null}>
                                <CodexSearchListMockup />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/funnel-formats"
                        element={
                            <Suspense fallback={null}>
                                <FunnelFormatsPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/glm/funnel-formats"
                        element={
                            <Suspense fallback={null}>
                                <GlmFunnelFormats />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/glm-search-list"
                        element={
                            <Suspense fallback={null}>
                                <GlmSearchListIndex />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/glm-search-list/rich"
                        element={
                            <Suspense fallback={null}>
                                <GlmSearchListRich />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/glm-search-list/compact"
                        element={
                            <Suspense fallback={null}>
                                <GlmSearchListCompact />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/glm-search-list/board"
                        element={
                            <Suspense fallback={null}>
                                <GlmSearchListBoard />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/glm-search-list/split"
                        element={
                            <Suspense fallback={null}>
                                <GlmSearchListSplit />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/icp-sentence"
                        element={
                            <Suspense fallback={null}>
                                <IcpMockupSentence />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/icp-funnel"
                        element={
                            <Suspense fallback={null}>
                                <IcpMockupFunnel />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/icp-swipe"
                        element={
                            <Suspense fallback={null}>
                                <IcpMockupSwipe />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/icp-dial"
                        element={
                            <Suspense fallback={null}>
                                <IcpMockupDial />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/icp-annotated"
                        element={
                            <Suspense fallback={null}>
                                <IcpMockupAnnotated />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/icp-console"
                        element={
                            <Suspense fallback={null}>
                                <IcpMockupConsole />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/icp-pipeline"
                        element={
                            <Suspense fallback={null}>
                                <IcpMockupPipeline />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/icp-spec"
                        element={
                            <Suspense fallback={null}>
                                <IcpMockupSpec />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/icp-pipeline-a"
                        element={
                            <Suspense fallback={null}>
                                <IcpMockupPipelineA />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/icp-pipeline-a1"
                        element={
                            <Suspense fallback={null}>
                                <IcpMockupPipelineA1 />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/icp-pipeline-b"
                        element={
                            <Suspense fallback={null}>
                                <IcpMockupPipelineB />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/icp-pipeline-c"
                        element={
                            <Suspense fallback={null}>
                                <IcpMockupPipelineC />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/chat-bubbles"
                        element={
                            <Suspense fallback={null}>
                                <ChatBubbleStyles />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/key-actions"
                        element={
                            <Suspense fallback={null}>
                                <KeyActionsMockup />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/edit-final-premium"
                        element={
                            <Suspense fallback={null}>
                                <EditFinalPremiumMockup />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/edit-final-cards"
                        element={
                            <Suspense fallback={null}>
                                <EditFinalCardsMockup />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/edit-final-cards-v2"
                        element={
                            <Suspense fallback={null}>
                                <EditFinalCardsV2 />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/edit-final-cards-v3"
                        element={
                            <Suspense fallback={null}>
                                <EditFinalCardsV3 />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/edit-final-cards-v4"
                        element={
                            <Suspense fallback={null}>
                                <EditFinalCardsV4 />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/edit-final-cards-v5"
                        element={
                            <Suspense fallback={null}>
                                <EditFinalCardsV5 />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/pipeline-chips"
                        element={
                            <Suspense fallback={null}>
                                <PipelineChipsMockup />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/edit-final-cards-v6"
                        element={
                            <Suspense fallback={null}>
                                <EditFinalCardsV6 />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/hero-animations"
                        element={
                            <Suspense fallback={null}>
                                <HeroAnimations />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/question-prompt"
                        element={
                            <Suspense fallback={null}>
                                <QuestionPromptIndex />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/question-prompt/compact-card"
                        element={
                            <Suspense fallback={null}>
                                <QuestionPromptCompactCard />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/question-prompt/chip-row"
                        element={
                            <Suspense fallback={null}>
                                <QuestionPromptChipRow />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/question-prompt/bubble"
                        element={
                            <Suspense fallback={null}>
                                <QuestionPromptBubble />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/glm5-2"
                        element={
                            <Suspense fallback={null}>
                                <Glm52Index />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/glm5-2/sourcing-studio"
                        element={
                            <Suspense fallback={null}>
                                <Glm52SourcingStudio />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/mockups/glm5-2/flow"
                        element={
                            <Suspense fallback={null}>
                                <Glm52SearchFlow />
                            </Suspense>
                        }
                    />
                    <Route element={<RequireAuth />}>
                        <Route
                            path="/custom-avatar"
                            element={
                                <Suspense fallback={null}>
                                    <CustomAvatarPage />
                                </Suspense>
                            }
                        />
                        <Route path="/admin/dashboard" element={<Navigate to="/admin/dashboard/overview" replace />} />
                        <Route path="/admin/dashboard/:section" element={<AdminDashboardRoute />} />
                        <Route path="/admin/dashboard/:section/:activityType" element={<AdminDashboardRoute />} />
                        <Route
                            path="/admin/dashboard/organizations/:organizationId/:section"
                            element={<AdminDashboardRoute />}
                        />
                        <Route
                            path="/admin/dashboard/organizations/:organizationId/:section/:activityType"
                            element={<AdminDashboardRoute />}
                        />
                        <Route
                            path="/admin/dashboard/organizations/:organizationId/members/:memberId/:section"
                            element={<AdminDashboardRoute />}
                        />
                        <Route
                            path="/admin/dashboard/organizations/:organizationId/members/:memberId/:section/:activityType"
                            element={<AdminDashboardRoute />}
                        />
                        {/* Full-page job launch/edit — authed, no AppShell chrome. */}
                        <Route
                            path="/v2/jobs/:id/edit-final"
                            element={
                                <Suspense fallback={null}>
                                    <JobEditFinalPage />
                                </Suspense>
                            }
                        />
                        {/* Agentic job workspace — authed, no AppShell chrome. */}
                        <Route
                            path="/v2/jobs/:id/manage"
                            element={
                                <Suspense fallback={null}>
                                    <JobManagePage />
                                </Suspense>
                            }
                        />
                        {/* Manage v2 surfaces use the platform gateway while leaving the
                established production create flow untouched. The routes always
                EXIST; who is sent to them is a per-org runtime decision
                (`isManageV2Org` — the cohort id list or the `manage-v2`
                preset), and the gateway authorizes by org regardless. There is
                deliberately no build-time flag: one gate, not two. */}
                        <Route
                            path="/v2/jobs/new/manage-v2"
                            element={
                                <Suspense fallback={null}>
                                    <NewJobManageV2Page />
                                </Suspense>
                            }
                        />
                        <Route
                            path="/v2/jobs/:id/manage-v2"
                            element={
                                <Suspense fallback={null}>
                                    <JobManageV2Page />
                                </Suspense>
                            }
                        />
                        {/* Lazy chunks, so an org that never routes here never loads them. */}
                        {/* Unbound session from the home-hero handoff. Two
                backends by org (see SessionRoute): the platform cohort
                creates a real ATS Job and lands in manage-v2; everyone
                else keeps the agent-worker chat that binds via
                create_job_role and lands in manage v1. */}
                        <Route
                            path="/session/:chatId"
                            element={
                                <Suspense fallback={null}>
                                    <SessionRoute />
                                </Suspense>
                            }
                        />
                        <Route element={<AppShell />}>
                            {appRoutes.map((route) => (
                                <Route key={route.id} path={route.path} element={route.element} />
                            ))}
                            <Route path="/v2" element={<Navigate to="/v2/jobs" replace />} />
                            <Route path="*" element={<NotFoundPage />} />
                        </Route>
                    </Route>
                </Routes>
                <ToastContainer />
            </Router>
        </AuthProvider>
    );
}
